import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { useMoveClientProject, type ClientProject } from "@/hooks/useClientProjects";
import { useEpics, useSprints, useBacklogItems } from "@/hooks/useProjectPlanning";
import { BacklogView } from "./BacklogView";
import { SprintBoard } from "./SprintBoard";
import { SprintsPanel } from "./SprintsPanel";
import { EpicsPanel } from "./EpicsPanel";

export function ProjectWorkspace({
  project,
  onBack,
}: {
  project: ClientProject;
  onBack: () => void;
}) {
  const { data: epics = [], isLoading: epicsLoading } = useEpics(project.id);
  const { data: sprints = [], isLoading: sprintsLoading } = useSprints(project.id);
  const { data: items = [], isLoading: itemsLoading } = useBacklogItems(project.id);
  const moveProject = useMoveClientProject();
  const [confirmDone, setConfirmDone] = useState(false);

  const isLoading = epicsLoading || sprintsLoading || itemsLoading;
  const openItems = items.filter((i) => i.status !== "done").length;
  const isDone = project.phase === "done";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onBack}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-[200px] flex-1">
          <p className="flex items-center gap-2 text-sm font-semibold">
            {project.title}
            {isDone && <Badge className="border-transparent bg-ok-soft text-ok">Concluído</Badge>}
          </p>
          <p className="text-xs text-muted-foreground">Planejamento e execução do projeto</p>
        </div>
        {/* The Jira workspace never wrote back to client_projects.phase, so
            projects managed here could never reach "done" (and the DB
            triggers that hang off it — budget accepted, portfolio draft —
            never fired). This is the explicit way to close one. */}
        {!isDone && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => setConfirmDone(true)}
          >
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Concluir projeto
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="board">
          <TabsList>
            <TabsTrigger value="backlog">Backlog</TabsTrigger>
            <TabsTrigger value="board">Board</TabsTrigger>
            <TabsTrigger value="sprints">Sprints</TabsTrigger>
            <TabsTrigger value="epics">Épicos</TabsTrigger>
          </TabsList>
          <TabsContent value="backlog" className="pt-3">
            <BacklogView
              clientProjectId={project.id}
              items={items}
              epics={epics}
              sprints={sprints}
            />
          </TabsContent>
          <TabsContent value="board" className="pt-3">
            <SprintBoard
              clientProjectId={project.id}
              items={items}
              epics={epics}
              sprints={sprints}
            />
          </TabsContent>
          <TabsContent value="sprints" className="pt-3">
            <SprintsPanel clientProjectId={project.id} items={items} />
          </TabsContent>
          <TabsContent value="epics" className="pt-3">
            <EpicsPanel clientProjectId={project.id} items={items} />
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={confirmDone} onOpenChange={setConfirmDone}>
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Concluir "{project.title}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              O projeto vai para a coluna "Concluído" e um rascunho de case é criado no portfólio
              para você revisar.
              {openItems > 0 &&
                ` Ainda há ${openItems} item(ns) do backlog em aberto — eles continuam acessíveis aqui.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => {
                moveProject.mutate({ projectId: project.id, newPhase: "done", newOrder: 0 });
                setConfirmDone(false);
              }}
            >
              Concluir projeto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
